import type { HighlightStatus } from '@tyro/api';
import dayjs from 'dayjs';
import type { InteractiveValues } from '../components/report-with-filters';

export enum Report {
  AWOL = 'awol',
  SUBSTITUTION = 'substitution',
  OCTOBER_RETURNS = 'october-returns',
  OVERDUE_LESSON_ATTENDANCE = 'overdue-lesson-attendance',
  SESSION_ATTENDANCE = 'session-attendance',
  HISTORIC_ATTENDANCE = 'historic-attendance',
  STUDENT_BEHAVIOUR = 'student-behaviour',
  STUDENT_DATA = 'student-data',
  STUDENT_PRIORITY_NOTES = 'student-priority-notes',
  SUBJECT_GROUP_ATTENDANCE_DYNAMIC = 'subject-group-attendance-dynamic',
  SUBJECT_STATS_OCTOBER_RETURNS = 'subject-stats-october-returns',
  SUBJECT_STUDENT_COUNT_MATRIX = 'subject-student-count-matrix',
  TEACHER_HOURS = 'teacher-hours',
  TUSLA = 'tusla',
  STAFF_HISTORIC_SEARCH = 'staff-historic-search',
  STUDENT_HISTORIC_SEARCH = 'student-historic-search',
  STUDENT_HISTORIC_CONTACT = 'historic-contacts',
  ASSESSMENT_RESULT = 'assessment-result',
  HISTORIC_ASSESSMENT_RESULT = 'historic-assessment-result',
  HIGHLIGHTS = 'highlights',
  HISTORIC_SIBLINGS = 'historic-siblings',
}

const reportUrls = {
  [Report.AWOL]: 'awol-students',
  [Report.SUBSTITUTION]: 'substitution-list',
  [Report.OCTOBER_RETURNS]: 'october-returns',
  [Report.OVERDUE_LESSON_ATTENDANCE]: 'overdue-lesson-attendance',
  [Report.SESSION_ATTENDANCE]: 'session-attendance',
  [Report.HISTORIC_ATTENDANCE]: 'historic-attendance',
  [Report.STUDENT_BEHAVIOUR]: 'student-behaviour',
  [Report.STUDENT_DATA]: 'student-data',
  [Report.STUDENT_PRIORITY_NOTES]: 'student-priority-notes',
  [Report.SUBJECT_GROUP_ATTENDANCE_DYNAMIC]: 'subject-group-attendance-dynamic',
  [Report.SUBJECT_STATS_OCTOBER_RETURNS]: 'subject-stats-october-returns',
  [Report.SUBJECT_STUDENT_COUNT_MATRIX]: 'subject-student-count-matrix',
  [Report.TEACHER_HOURS]: 'teacher-hours',
  [Report.TUSLA]: 'tusla',
  [Report.STAFF_HISTORIC_SEARCH]: 'staff-historic-search',
  [Report.STUDENT_HISTORIC_SEARCH]: 'student-historic-search',
  [Report.STUDENT_HISTORIC_CONTACT]: 'historic-contacts',
  [Report.ASSESSMENT_RESULT]: 'assessment-result',
  [Report.HISTORIC_ASSESSMENT_RESULT]: 'historic-assessment-result',
  [Report.HIGHLIGHTS]: 'highlights',
  [Report.HISTORIC_SIBLINGS]: 'historic-siblings',
} as const;

const tabs = {
  [Report.OCTOBER_RETURNS]: {
    'cover-stats': 'course-stats-october-returns',
    'school-stats': 'school-stats-october-returns',
    'subject-stats': 'subject-stats-october-returns',
  },
} as const;

const defaultTabs = {
  [Report.OCTOBER_RETURNS]: tabs[Report.OCTOBER_RETURNS]['cover-stats'],
} as const;

export type ReportTabs = typeof tabs;

export type ReportLinkOptions =
  | {
      report: Report.AWOL;
    }
  | {
      report: Report.SUBSTITUTION;
    }
  | {
      report: Report.OCTOBER_RETURNS;
      tab?: keyof ReportTabs[Report.OCTOBER_RETURNS];
    }
  | {
      report: Report.OVERDUE_LESSON_ATTENDANCE;
      filters?: {
        to_date?: dayjs.Dayjs;
        from_date?: dayjs.Dayjs;
        staff?: Array<number>;
      };
    }
  | {
      report: Report.SESSION_ATTENDANCE;
    }
  | {
      report: Report.STUDENT_BEHAVIOUR;
      filters?: {
        to_date?: dayjs.Dayjs;
        from_date?: dayjs.Dayjs;
        class?: Array<string | number>;
        behaviour_category_id?: Array<string | number>;
        tags?: Array<string | number>;
      };
    }
  | {
      report: Report.STUDENT_DATA;
    }
  | {
      report: Report.STUDENT_PRIORITY_NOTES;
      filters?: {
        to_date?: dayjs.Dayjs;
        from_date?: dayjs.Dayjs;
        only_my_groups?: boolean;
      };
    }
  | {
      report: Report.SUBJECT_GROUP_ATTENDANCE_DYNAMIC;
      filters?: {
        to_date?: dayjs.Dayjs;
        from_date?: dayjs.Dayjs;
        students?: Array<number>;
      } & InteractiveValues;
    }
  | {
      report: Report.SUBJECT_STATS_OCTOBER_RETURNS;
    }
  | {
      report: Report.SUBJECT_STUDENT_COUNT_MATRIX;
    }
  | {
      report: Report.TEACHER_HOURS;
    }
  | {
      report: Report.TUSLA;
    }
  | {
      report: Report.STAFF_HISTORIC_SEARCH;
    }
  | {
      report: Report.STUDENT_HISTORIC_SEARCH;
    }
  | {
      report: Report.STUDENT_HISTORIC_CONTACT;
    }
  | {
      report: Report.ASSESSMENT_RESULT;
    }
  | {
      report: Report.HISTORIC_ASSESSMENT_RESULT;
    }
  | {
      report: Report.HISTORIC_ATTENDANCE;
    }
  | {
      report: Report.HIGHLIGHTS;
      filters?: {
        to_date?: dayjs.Dayjs;
        from_date?: dayjs.Dayjs;
        staff?: Array<number>;
        status?: Array<HighlightStatus>;
      };
    }
  | {
      report: Report.HISTORIC_SIBLINGS;
    };

export function getReportUrl({ report, ...options }: ReportLinkOptions) {
  const reportId = reportUrls[report];
  let tab = report === Report.AWOL ? '' : reportUrls[report];
  let filters = '';

  if (report in tabs && report in defaultTabs) {
    // @ts-expect-error
    tab = tabs[report]?.[options.tab] || defaultTabs[report];
  }

  if ('filters' in options && Object.keys(options?.filters ?? {}).length > 0) {
    filters = Object.entries(options?.filters ?? {}).reduce(
      (acc, [key, value], index) => {
        const typedValue = value as
          | string
          | number
          | Array<string | number>
          | dayjs.Dayjs;
        let formattedValue = typedValue.toString();

        if (Array.isArray(typedValue)) {
          formattedValue = typedValue.join(',');
        } else if (dayjs.isDayjs(typedValue)) {
          formattedValue = typedValue.format('YYYY-MM-DD');
        }

        if (formattedValue === '') return acc;

        return index === 0
          ? `${acc}${key}=${formattedValue}`
          : `${acc}&${key}=${formattedValue}`;
      },
      '?',
    );
  }

  return `/reports/${reportId}/${tab}${filters}`;
}
